import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuditLog } from '@/hooks/useAuditLog';
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

export const useAdminModels = () => {
  return useQuery({
    queryKey: ['admin-llm-models'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('llm_models')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
};

export const useCreateModel = () => {
  const queryClient = useQueryClient();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async (model: TablesInsert<'llm_models'>) => {
      const { data, error } = await (supabase as any)
        .from('llm_models')
        .insert(model)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin-llm-models'] });
      queryClient.invalidateQueries({ queryKey: ['llm_models'] });
      logAction({ action: 'create_model', targetType: 'model', targetId: data.id, metadata: { display_name: data.display_name } });
    },
  });
};

export const useUpdateModel = () => {
  const queryClient = useQueryClient();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async ({ id, ...updates }: TablesUpdate<'llm_models'> & { id: string }) => {
      const { data, error } = await (supabase as any)
        .from('llm_models')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin-llm-models'] });
      queryClient.invalidateQueries({ queryKey: ['llm_models'] });
      logAction({ action: 'update_model', targetType: 'model', targetId: data.id });
    },
  });
};

export const useDeleteModel = () => {
  const queryClient = useQueryClient();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('llm_models')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: (id: string) => {
      queryClient.invalidateQueries({ queryKey: ['admin-llm-models'] });
      queryClient.invalidateQueries({ queryKey: ['llm_models'] });
      logAction({ action: 'delete_model', targetType: 'model', targetId: id });
    },
  });
};

export const useSyncModels = () => {
  const queryClient = useQueryClient();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('sync-models');
      if (error) throw error;
      return data as { inserted: number; updated: number; total: number };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-llm-models'] });
      queryClient.invalidateQueries({ queryKey: ['llm_models'] });
      logAction({ action: 'sync_models', targetType: 'model', metadata: { inserted: data.inserted, updated: data.updated, total: data.total } });
    },
  });
};
